import React, { useState } from 'react';
import {
  AccordionItem,
  AppButton,
  Box,
  costFormater,
  DeductibleType,
  GagueChart,
  Inscription,
  NoteCard,
  NoteIcon,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { NotesButtonWrapper } from '@/components/wrappers/NotesButtonWrapper.tsx';
import { EditDeductibleModal } from '@/modules/deductibles/EditDeductibleModal';
import { useEditDeductibleModalStore } from '@/modules/deductibles/EditDeductibleModal/stores/useEditDeductibleModal.store.ts';
import theme from '@/styles/theme';

import { DeductibleUser } from './hooks/useDependents';
import EmbeddedOnlySpendTracker from './EmbeddedOnlySpendTracker';
import { useSpendTrackerLogic } from './hooks';
import SpendTrackerUsersList from './SpendTrackerUsersList';
import TierPeriodsTable from './TierPeriodsTable';

const AccordionWrapper = styled(Box)`
  transition: all 0.15s;
  > div {
    width: 100%;
  }
`;

interface Props {
  employeeUser: DeductibleUser;
  isIndividual?: boolean;
  defaultHealthPlanYearId?: string;
  isDetailsPageMode?: boolean;
  onHealthPlanNameChange?: (healthPlanName: string) => void;
  onHealthPlanIdChange?: (healthPlanId: string) => void;
  onHealthPlanCoverageLevelChange?: (coverageLevel: string) => void;
}
const SpendTracker: React.FC<Props> = ({
  employeeUser,
  isIndividual,
  defaultHealthPlanYearId,
  isDetailsPageMode,
  onHealthPlanNameChange,
  onHealthPlanIdChange,
  onHealthPlanCoverageLevelChange,
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const openEditDeductibleModal = useEditDeductibleModalStore((state) => state.state.visible);

  const {
    formattedData,
    showNotes,
    selectedHealthPlanYearId,
    setHealthPlanYearId,
    hasAccess,
    healthPlanYearOptions,
    isPlanYearsLoading,
    handleShowNotes,
    handleOpenTrackerDetails,
    chartValues,
    chartTiersValues,
    remaining,
    total,
    isLoading,
    notes,
    users,
    individualTotal,
    individualTotalAccumulatedValues,
    individualAccumulatedValues,
    familyAccumulatedValue,
    formattedTiersData,
    coverageLevelLabel,
    isDeductiblesLoading,
    healthPolicyId,
    isEmbeddedOnly: isEmbeddedOnlyTriggers,
  } = useSpendTrackerLogic({
    employeeUser,
    isIndividual,
    defaultHealthPlanYearId,
    onHealthPlanNameChange,
    onHealthPlanIdChange,
    onHealthPlanCoverageLevelChange,
  });
  const isEmbeddedOnly = !isLoading && total <= 0 && isEmbeddedOnlyTriggers;

  if (!isLoading && total <= 0 && !isEmbeddedOnlyTriggers) {
    return null;
  }

  return (
    <>
      <Box background="module" round="moduleRound" pad="l" margin={{ top: 'l', bottom: 'm' }}>
        <Box direction="row" align="center" justify="between">
          <Text size="large" color="textTitle" margin={{ right: 'xs' }} weight={700}>
            Spend Tracker
          </Text>
          <Box direction="row" gap="m">
            {!isIndividual && (
              <Box direction="row" gap="m">
                {formattedData?.length ? (
                  <Box width="230px" margin={{ left: 'spacing8' }}>
                    <SelectDropdown
                      id="healthPlanYears"
                      testId="health-plan-years"
                      name="healthPlanYears"
                      isLoading={isPlanYearsLoading}
                      singleMode
                      activeTitle
                      options={healthPlanYearOptions}
                      value={selectedHealthPlanYearId}
                      onChange={setHealthPlanYearId}
                    />
                  </Box>
                ) : null}
                {!isDetailsPageMode && (
                  <AppButton
                    buttonType="secondary"
                    type="submit"
                    testId="open-spend-tracker-details"
                    onClick={handleOpenTrackerDetails}
                    width="120px"
                  >
                    More Info
                  </AppButton>
                )}
              </Box>
            )}
            {notes && hasAccess && (
              <NotesButtonWrapper onClick={handleShowNotes} isActive={showNotes}>
                <NoteIcon />
              </NotesButtonWrapper>
            )}
          </Box>
        </Box>
        {showNotes && (
          <NoteCard
            width="100%"
            text={notes}
            styles={{ marginTop: theme.spacings.l }}
            title="Health Plan notes"
            onClose={handleShowNotes}
          />
        )}
        {isEmbeddedOnly ? (
          <EmbeddedOnlySpendTracker
            employeeUser={employeeUser}
            coverageLevelLabel={coverageLevelLabel}
            users={users}
            selectedHealthPlanYearId={selectedHealthPlanYearId}
            individualTotal={individualTotal}
          />
        ) : (
          <Box margin={{ top: showNotes ? 'm' : 'l' }} direction="row">
            <Box
              elevation="default"
              fill
              width={{ max: '360px' }}
              round="container1Round"
              background="canvas"
            >
              <GagueChart items={chartValues} tiers={chartTiersValues}>
                <Box direction="column" align="center" justify="end" margin={{ top: 'auto' }}>
                  <Text weight={400} color="textSecondary">
                    Remaining
                  </Text>
                  <Text size="2xl" weight={700}>
                    {costFormater(remaining, { isPrecision: true })}
                  </Text>
                  <Text>of {costFormater(total, { isPrecision: true })}</Text>
                </Box>
              </GagueChart>
            </Box>
            <Box
              direction="column"
              elevation="default"
              round="container1Round"
              fill
              background="canvas"
              style={{ height: 'auto' }}
              margin={{ left: 'm' }}
              pad="l"
            >
              <Inscription
                weight="bold"
                margin={{ bottom: 'xs' }}
              >{`Coverage level: ${coverageLevelLabel || ''}`}</Inscription>
              <Box background="module" round="container1Round" pad="s">
                <Box direction="row" round="container1Round" background="canvas" pad="s">
                  <Box direction="row" width="50%">
                    <Text size="large">{isIndividual ? 'Individual Deductible:' : 'Deductible:'}</Text>
                    <Inscription
                      size="large"
                      margin={{ left: 'xs' }}
                      weight={700}
                      whiteSpace="nowrap"
                      hasEllipsisMode
                    >
                      {costFormater(total, { isPrecision: true })}
                    </Inscription>
                  </Box>
                  <Box direction="row" width="50%">
                    <Text size="large">Remaining:</Text>
                    <Inscription
                      size="large"
                      margin={{ left: 'xs' }}
                      weight={700}
                      whiteSpace="nowrap"
                      hasEllipsisMode
                    >
                      {costFormater(remaining, { isPrecision: true })}
                    </Inscription>
                  </Box>
                </Box>
              </Box>
              {!isIndividual && (
                <SpendTrackerUsersList
                  employeeUser={employeeUser}
                  users={users}
                  disabled={!individualTotal}
                  selectedHealthPlanYearId={selectedHealthPlanYearId}
                />
              )}
            </Box>
          </Box>
        )}
        {!isEmbeddedOnly && (
          <AccordionWrapper
            pad="m"
            direction="row"
            justify="between"
            align="center"
            background="canvas"
            round="container1Round"
            margin={{ top: 'm' }}
          >
            <AccordionItem
              onChange={() => setExpanded(!isExpanded)}
              testId="tiers-module-item"
              iconSize="24px"
              isExpanded={isExpanded}
              text={
                <Inscription
                  weight={400}
                  margin={{ left: 'xxs' }}
                  color="textBody"
                  size="16px"
                  lineHeight="22px"
                >
                  Tiers coverage periods
                </Inscription>
              }
              isLeftArrow
            >
              <TierPeriodsTable formattedTiers={formattedTiersData} isLoading={isDeductiblesLoading} />
            </AccordionItem>
          </AccordionWrapper>
        )}
      </Box>
      {openEditDeductibleModal && (
        <EditDeductibleModal
          visible={openEditDeductibleModal}
          isLoading={isDeductiblesLoading}
          mode={individualTotal ? DeductibleType.Individual : DeductibleType.Family}
          familyValue={individualTotal ? individualTotalAccumulatedValues : familyAccumulatedValue}
          healthPolicyId={healthPolicyId}
          members={users}
          individualAccumulatedValues={individualAccumulatedValues}
        />
      )}
    </>
  );
};

export default React.memo(SpendTracker);
